// src/components/common/ErrorBoundary.js
import React from 'react';
import { Box, Button, Typography, Paper, Container, Alert } from '@mui/material';
import { ErrorOutline, Refresh, Home } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Log to external service if configured
    this.logErrorToService(error, errorInfo);

    // If too many errors, might indicate a render loop
    if (this.state.errorCount > 5) {
      console.error('Too many errors detected, possible render loop');
    }
  }

  logErrorToService = (error, errorInfo) => {
    // TODO: Send to Application Insights or error tracking service
    try {
      const errorData = {
        message: error.toString(),
        stack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent
      };

      // For now, just log to console
      // In production, send to Application Insights
      console.log('Error logged:', errorData);
    } catch (loggingError) {
      console.error('Failed to log error:', loggingError);
    }
  };

  handleReset = () => {
    // Reset error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // If onReset callback provided, call it
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          resetError: this.handleReset
        });
      }

      // Default error UI
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorCount={this.state.errorCount}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
          onReload={this.handleReload}
          showDetails={process.env.NODE_ENV === 'development'}
        />
      );
    }

    return this.props.children;
  }
}

// Functional component for the fallback UI
const ErrorBoundaryFallback = ({ 
  error, 
  errorInfo, 
  errorCount,
  onReset, 
  onGoHome, 
  onReload,
  showDetails 
}) => {
  const { t } = useTranslation('common');

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%'
          }}
        >
          <ErrorOutline
            sx={{
              fontSize: 80,
              color: 'error.main',
              mb: 2
            }}
          />

          <Typography variant="h4" gutterBottom color="error">
            {t('errorBoundary.title', 'Oops! Something went wrong')}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('errorBoundary.message', 'We encountered an unexpected error. Please try one of the options below.')}
          </Typography>

          {errorCount > 3 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {t('errorBoundary.multipleErrors', 'Multiple errors detected. You may need to reload the page or clear your browser cache.')}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={onReset}
            >
              {t('errorBoundary.tryAgain', 'Try Again')}
            </Button>

            <Button
              variant="outlined"
              startIcon={<Home />}
              onClick={onGoHome}
            >
              {t('errorBoundary.goHome', 'Go Home')}
            </Button>

            <Button
              variant="outlined"
              onClick={onReload}
            >
              {t('errorBoundary.reload', 'Reload Page')}
            </Button>
          </Box>

          {showDetails && error && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 3,
                backgroundColor: 'grey.50',
                textAlign: 'left'
              }}
            >
              <Typography variant="subtitle2" color="error" gutterBottom>
                Error Details (Development Only):
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '0.75rem',
                  mb: 2
                }}
              >
                {error.toString()}
              </Typography>
              
              {errorInfo && (
                <>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Component Stack:
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.65rem',
                      maxHeight: 200,
                      overflow: 'auto'
                    }}
                  >
                    {errorInfo.componentStack}
                  </Typography>
                </>
              )}
            </Paper>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {t('errorBoundary.support', 'If this problem persists, please contact support.')}
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default ErrorBoundary;
